import { IPublicBasket } from "@app-contexts/public/features/basket/interfaces/IPublicBasket";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicEventsService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import usePublicProductsService from "@app-contexts/public/features/products/data/services/usePublicProductsService";
import {
  ProductTypeSerializer,
  ProductVariantSerializer,
} from "@app-contexts/public/features/products/data/types/products";
import { common } from "@shared/constants";
import { ITEM_TYPES_MAP } from "@shared/constants/enums";
import { X_SP_UUID } from "@shared/env";
import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import {
  GtagBasketType,
  GtagItemGroupType,
  GtagItemType,
} from "@shared/lib/analytics/types/analytics-types";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { TicketBasket, TicketBasketType } from "@shared/modules/basket/types";
import {
  PublicBaseEventSerializer,
  TicketTypeSerializer,
} from "@shared/modules/events/types/public";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import {
  PublicMembershipGroupSerializer,
  PublicMembershipLevelSerializer,
} from "@shared/modules/memberships/types";
import { useStore } from "@shared/store/useStore";
import { getFlattenedData } from "@shared/utils/data/data-utils";
import { logAppInfo } from "@shared/utils/errors/errorLogger";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

export type CheckoutTrackingContextType = {
  itemTracking: Array<GtagItemType>;
  updateItemTracking: (input: GtagItemType) => void;
  getBasketWithTrackingData: (
    inputBasket: TicketBasket,
    itemTracking: Array<GtagItemType>
  ) => GtagBasketType;
  initializeTracking: () => Promise<boolean>;
  disableAnalytics: () => void;
};

const CheckoutTrackingContext =
  createContext<CheckoutTrackingContextType | null>(null);

// New utility functions outside the component
const createEventMaps = (events: PublicBaseEventSerializer[]) => {
  const eventMap = events.reduce<Record<number, PublicBaseEventSerializer>>(
    (acc, event) => {
      acc[event.id] = event;
      return acc;
    },
    {}
  );

  const ticketTypeMap = events
    .flatMap((event) => event.ticket_types)
    .filter((ticket): ticket is TicketTypeSerializer => Boolean(ticket))
    .reduce<Record<number, TicketTypeSerializer>>((acc, ticket) => {
      acc[ticket.id] = ticket;
      return acc;
    }, {});

  return { eventMap, ticketTypeMap };
};

const createProductMaps = (products: ProductTypeSerializer[]) => {
  const productMap = products.reduce<Record<number, ProductTypeSerializer>>(
    (acc, product) => {
      acc[product.id] = product;
      return acc;
    },
    {}
  );

  const productAttributeMap = products
    .flatMap((product) => product.product_attributes)
    .filter((attr): attr is ProductVariantSerializer => Boolean(attr))
    .reduce<Record<number, ProductVariantSerializer>>((acc, attr) => {
      acc[attr.id] = attr;
      return acc;
    }, {});

  return { productMap, productAttributeMap };
};

const createMembershipGroupMaps = (
  membershipGroups: PublicMembershipGroupSerializer[]
) => {
  const membershipGroupMap = membershipGroups.reduce<
    Record<number, PublicMembershipGroupSerializer>
  >((acc, membershipGroup) => {
    acc[membershipGroup.id] = membershipGroup;
    return acc;
  }, {});

  const membershipLevelMap = membershipGroups
    .flatMap((membershipGroup) => membershipGroup.membership_levels)
    .filter(
      (membershipLevel): membershipLevel is PublicMembershipLevelSerializer =>
        Boolean(membershipLevel)
    )
    .reduce<Record<number, PublicMembershipLevelSerializer>>(
      (acc, membershipLevel) => {
        acc[membershipLevel.id] = membershipLevel;
        return acc;
      },
      {}
    );

  return { membershipGroupMap, membershipLevelMap };
};
export const CheckoutTrackingProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const { basketEventIds, basketMembershipIds } =
    useBasketService<IPublicBasket>();

  // stores tracking data that was previously fetched in the ItemGroup.web component
  // examples: the names of events/memberships/products, etc.
  // this is required because basket.item_groups doesn't have access to this information
  const [itemTracking, setItemTracking] = useState<Array<GtagItemType>>([]);

  const isTrackingInitializedRef = useRef(false);

  const updateItemTracking = (input: GtagItemType) => {
    if (
      !itemTracking.find(
        (ig) => ig.item_id === input.item_id && ig.item_type === input.item_type
      )
    ) {
      setItemTracking([...itemTracking, input]);
    }
  };

  const basket: (TicketBasketType & { [X_SP_UUID]?: string }) | undefined =
    useStore.getState().getBasket(common.DataProvider.Public);

  const productIdsInBasket = useMemo(() => {
    return basket?.item_groups?.reduce<Array<number>>((aggregate, ig) => {
      if (
        ig.item_type === ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id &&
        ig.product_attribute
      ) {
        return [...aggregate, ig.product_attribute];
      }
      return aggregate;
    }, []);
  }, [basket?.item_groups]);

  const { usePaginated } = usePublicEventsService();

  const { data: eventsInBasket, isFetching: isEventsFetching } = usePaginated({
    params: {
      id__in: basketEventIds?.join(",") || "",
    },
    options: { enabled: Boolean(basketEventIds && basketEventIds?.length > 0) },
  });

  const flattenedEvents = useMemo(() => {
    if (!eventsInBasket) return [];

    return getFlattenedData(eventsInBasket);
  }, [eventsInBasket]);

  const { usePaginated: usePaginatedProducts } = usePublicProductsService();

  const { data: productsData, isFetching: isProductsFetching } =
    usePaginatedProducts({
      params: {
        event: basketEventIds?.join(",") || "",
        id__in: productIdsInBasket?.join(",") || "",
      },
      options: {
        enabled: Boolean(productIdsInBasket && productIdsInBasket?.length > 0),
      },
    });

  const flattenedProductsArray = useMemo(() => {
    if (!productsData) return [];

    return getFlattenedData(productsData);
  }, [productsData]);

  const { usePaginated: usePaginatedMembershipGroups } =
    usePublicMembershipGroupsService();

  const { data: membershipGroupsData, isFetching: isMembershipGroupsFetching } =
    usePaginatedMembershipGroups({
      params: {
        id__in: basketMembershipIds?.join(",") || "",
      },
      options: {
        enabled: Boolean(
          basketMembershipIds && basketMembershipIds?.length > 0
        ),
      },
    });

  const flattenedMembershipGroupsArray = useMemo(() => {
    if (!membershipGroupsData) return [];

    return getFlattenedData(membershipGroupsData);
  }, [membershipGroupsData]);

  const { paymentVenue, triggerCheckoutTrackingTokens } = usePublicBasket();
  const analytics = useAnalytics();

  // fetch venue data
  const { useRetrieve } = usePublicVenueService();
  const { data: venueData } = useRetrieve(paymentVenue, {
    options: { enabled: Boolean(paymentVenue) },
  });

  // inserts the stored tracking data into basket.item_groups
  const getBasketWithTrackingData = useCallback(
    (inputBasket: TicketBasket, itemTracking: Array<GtagItemType>) => {
      const newItemGroups = inputBasket?.item_groups?.map((ig) => {
        const trackingData = itemTracking.find(
          (item) => item.item_num_id === ig.id
        );
        if (trackingData) {
          return { ...ig, ...trackingData };
        }
        return ig as GtagItemGroupType;
      });
      return { ...inputBasket, item_groups: newItemGroups };
    },
    []
  );

  const initializeTracking = useCallback(async () => {
    const isFetchingData =
      isEventsFetching || isProductsFetching || isMembershipGroupsFetching;

    const isTrackingDataReady =
      venueData &&
      basket &&
      basket?.item_groups &&
      flattenedEvents &&
      flattenedProductsArray &&
      flattenedMembershipGroupsArray;

    if (
      isFetchingData ||
      !isTrackingDataReady ||
      isTrackingInitializedRef.current ||
      !(basket && basket.item_groups) // basket should have item_groups in order to track
    ) {
      return false;
    }

    const fbOpts = basket[X_SP_UUID] ? { uuid: basket[X_SP_UUID] } : null;

    analytics.initVenue(venueData);

    try {
      const { eventMap, ticketTypeMap } = createEventMaps(flattenedEvents);
      const { productMap, productAttributeMap } = createProductMaps(
        flattenedProductsArray
      );
      const { membershipGroupMap, membershipLevelMap } =
        createMembershipGroupMaps(flattenedMembershipGroupsArray);

      const trackingData = basket.item_groups
        .map((ig) => {
          if (ig.item_type === ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id) {
            const eventName = eventMap[ig.event]?.name;
            const ticketTypeName = ticketTypeMap[Number(ig.type)]?.name;

            return analytics.formatGtagTrackingData(
              Number(ig.id),
              eventName,
              ticketTypeName,
              ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
            );
          }

          if (ig.item_type === ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id) {
            const productName = productMap[Number(ig.product_attribute)]?.name;
            const variantName =
              productAttributeMap[Number(ig.product_attribute)]?.name;

            return analytics.formatGtagTrackingData(
              Number(ig.id),
              productName,
              variantName,
              ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id
            );
          }
          if (ig.item_type === ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id) {
            const membershipGroupName =
              membershipGroupMap[Number(ig.membership_group)]?.name;
            const membershipLevelName =
              membershipLevelMap[Number(ig.membership_level)]?.name;
            return analytics.formatGtagTrackingData(
              Number(ig.id),
              membershipGroupName,
              membershipLevelName,
              ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id
            );
          }
          return null;
        })
        .filter((item): item is GtagItemType => Boolean(item));

      const trackedBasket = getBasketWithTrackingData(basket, trackingData);

      analytics.beginCheckout(trackedBasket, venueData, fbOpts);
      triggerCheckoutTrackingTokens();
      isTrackingInitializedRef.current = true;
      return true;
    } catch (error) {
      logAppInfo(`[Checkout Tracking] Failed to initialize tracking: ${error}`);
      return false;
    }
  }, [
    venueData,
    basket,
    getBasketWithTrackingData,
    analytics,
    flattenedEvents,
    flattenedProductsArray,
    flattenedMembershipGroupsArray,
    triggerCheckoutTrackingTokens,
    isEventsFetching,
    isProductsFetching,
    isMembershipGroupsFetching,
  ]);

  const disableAnalytics = useCallback(() => {
    if (venueData) {
      analytics.disableVenue(venueData);
    }
  }, [venueData, analytics]);

  const value = {
    itemTracking,
    updateItemTracking,
    getBasketWithTrackingData,
    initializeTracking,
    disableAnalytics,
  };

  return (
    <CheckoutTrackingContext.Provider value={value}>
      {children}
    </CheckoutTrackingContext.Provider>
  );
};

export const useCheckoutTrackingContext = () => {
  const context = useContext(CheckoutTrackingContext);

  if (!context) {
    throw new Error(
      "useCheckoutTrackingContext must be used within a CheckoutTrackingProvider."
    );
  }
  return context;
};
